import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { PDFDownloadLink } from '@react-pdf/renderer';
import serviceMethods from '../../service';
import LoadingSpinner from '../LoadingSpinner';
import MultiSelectDropdown from '../MultiSelectDropdown';
import SelectDropdown from '../SelectDropdown';
import {
  GRANULARITIES,
  buildCustomPeriod,
  describePeriod,
  getDefaultPeriod,
  getPeriods,
  isValidCustomRange,
} from './reportPeriods';
import { useScheduler } from '../../contexts/scheduler';
import { useAuth } from '../../contexts/auth';
import ReportPDF from './ReportPDF';
import ReportJobs from './ReportJobs';
import {
  OPERATIONS_NOTES,
  PAYROLL_NOTES,
  STALENESS_NOTE,
  coverageText,
  formatRunAt,
  getFilterLines,
  getOperationsFigures,
  getSections,
  money,
  quantity,
  FINISH_LABELS,
  formatRates,
} from './format';

const ReportSection = ({
  heading,
  subheading,
  totals,
  taxRates,
  coverage,
  showYards,
  showBatches,
  showContractors,
  jobs,
}) => {
  const { costRows, totalCost, margin } = getOperationsFigures(totals, taxRates);

  return (
    <div className='w-full flex flex-col gap-4'>
      <div>
        <h3 className='font-bold text-dark-blue'>{heading}</h3>
        <p className='text-sm text-opaque-job-details-gray'>{subheading}</p>
      </div>

      <table className='w-full text-sm'>
        <tbody>
          <tr className='border-b border-navy-light'>
            <td className='py-1.5 font-semibold'>Total cost</td>
            <td className='py-1.5 text-right font-semibold'>{money(totalCost)}</td>
          </tr>
          {costRows.map(([label, value, share], index) => (
            <Fragment key={label}>
              <tr className='border-b border-navy-light'>
                <td className='py-1.5 pl-4'>{label}</td>
                <td className='py-1.5 text-right'>
                  {money(value)}{' '}
                  <span className='text-opaque-job-details-gray'>({share.toFixed(1)}%)</span>
                </td>
              </tr>
              {/* the first cost row is labor; runs saved before this figure existed have none */}
              {index === 0 && showContractors && totals.contractors !== undefined && (
                <tr className='border-b border-navy-light'>
                  <td className='py-1.5 pl-10'>Contractors</td>
                  <td className='py-1.5 pr-10 text-right'>{money(totals.contractors)}</td>
                </tr>
              )}
            </Fragment>
          ))}
          <tr className='border-b border-navy-light'>
            <td className='py-1.5 font-semibold'>Total sales</td>
            <td className='py-1.5 text-right font-semibold'>{money(totals.sales)}</td>
          </tr>
          <tr className='border-b border-navy-light'>
            <td className='py-1.5 font-semibold'>Gross profit</td>
            <td className='py-1.5 text-right font-semibold'>
              {money(totals.profit)}{' '}
              <span className='font-normal text-opaque-job-details-gray'>
                ({margin.toFixed(1)}%)
              </span>
            </td>
          </tr>
          <tr className='border-b border-navy-light'>
            <td className='py-1.5'>Pools finished</td>
            <td className='py-1.5 text-right'>{quantity(totals.poolsFinished)}</td>
          </tr>
          {showYards && (
            <tr className='border-b border-navy-light last:border-0'>
              <td className='py-1.5'>Yards</td>
              <td className='py-1.5 text-right'>{quantity(totals.yards)}</td>
            </tr>
          )}
          {showBatches && (
            <tr className='border-b border-navy-light last:border-0'>
              <td className='py-1.5'>Batches</td>
              <td className='py-1.5 text-right'>{quantity(totals.batches)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <p className='text-sm text-opaque-job-details-gray'>{coverageText(coverage)}</p>

      {jobs}
    </div>
  );
};

//Plaster branch employees carry their pay split by finish, shown as lines beneath their own. Runs
//from before the split, and gunite branches, have none, and so no Rate column either.
const PayeeTable = ({ payees, showPoolColumns }) => {
  const showRate = showPoolColumns && payees.some(payee => payee.finishes);

  return (
    <div className='w-full overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead>
          <tr className='border-b border-navy-light text-left'>
            <th className='py-1.5 font-semibold'>Name</th>
            <th className='py-1.5 font-semibold text-right'>Jobs</th>
            {showPoolColumns && (
              <>
                <th className='py-1.5 font-semibold text-right'>Pools</th>
                {showRate && <th className='py-1.5 pl-4 font-semibold text-right'>Rate</th>}
                <th className='py-1.5 font-semibold text-right'>Pool pay</th>
                <th className='py-1.5 font-semibold text-right'>Travel</th>
                <th className='py-1.5 font-semibold text-right'>Extras</th>
              </>
            )}
            <th className='py-1.5 font-semibold text-right'>Pay</th>
          </tr>
        </thead>
        <tbody>
          {payees.map(payee => (
            <Fragment key={payee.name}>
              <tr className={payee.finishes ? '' : 'border-b border-navy-light'}>
                <td className='py-1.5 pr-4'>{payee.name}</td>
                <td className='py-1.5 text-right'>{quantity(payee.jobs)}</td>
                {showPoolColumns && (
                  <>
                    <td className='py-1.5 text-right'>{quantity(payee.pools)}</td>
                    {showRate && <td />}
                    <td className='py-1.5 text-right'>{money(payee.poolPay)}</td>
                    <td className='py-1.5 text-right'>{money(payee.travel)}</td>
                    <td className='py-1.5 text-right'>{money(payee.extras)}</td>
                  </>
                )}
                <td className='py-1.5 text-right'>{money(payee.pay)}</td>
              </tr>
              {payee.finishes?.map((line, i) => (
                <tr
                  key={line.finish}
                  className={`text-opaque-job-details-gray ${i === payee.finishes.length - 1 ? 'border-b border-navy-light' : ''}`}
                >
                  <td className='py-1 pl-4 pr-4'>{FINISH_LABELS[line.finish]}</td>
                  <td className='py-1 text-right'>{quantity(line.jobs)}</td>
                  <td className='py-1 text-right'>{quantity(line.pools)}</td>
                  <td className='py-1 pl-4 text-right whitespace-nowrap'>
                    {formatRates(line.rates)}
                  </td>
                  <td className='py-1 text-right'>{money(line.poolPay)}</td>
                  <td className='py-1 text-right'>{money(line.travel)}</td>
                  <td className='py-1 text-right'>{money(line.extras)}</td>
                  <td className='py-1 text-right'>{money(line.pay)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PayrollSection = ({ heading, subheading, totals, coverage, employees, contractors }) => (
  <div className='w-full flex flex-col gap-4'>
    <div>
      <h3 className='font-bold text-dark-blue'>{heading}</h3>
      <p className='text-sm text-opaque-job-details-gray'>{subheading}</p>
    </div>

    <table className='w-full text-sm'>
      <tbody>
        <tr className='border-b border-navy-light'>
          <td className='py-1.5 font-semibold'>Employee pay</td>
          <td className='py-1.5 text-right font-semibold'>{money(totals.employeePay)}</td>
        </tr>
        <tr className='border-b border-navy-light'>
          <td className='py-1.5 font-semibold'>Contractor pay</td>
          <td className='py-1.5 text-right font-semibold'>{money(totals.contractorPay)}</td>
        </tr>
      </tbody>
    </table>

    {employees &&
      (employees.length ? (
        <PayeeTable payees={employees} showPoolColumns />
      ) : (
        <p className='text-sm text-opaque-job-details-gray'>
          No employees were paid in this period.
        </p>
      ))}

    {contractors?.length > 0 && (
      <div className='flex flex-col gap-2'>
        <p className='text-sm font-semibold text-dark-blue'>Contractors</p>
        <PayeeTable payees={contractors} />
      </div>
    )}

    <p className='text-sm text-opaque-job-details-gray'>{coverageText(coverage)}</p>
  </div>
);

const REPORT_TYPES = [
  {
    value: 'operations',
    type: 'OPERATIONS',
    label: 'Operations',
    title: 'Operations Report',
    description:
      "Jobs whose last scheduled shift falls in the selected period, costed from each job's submitted cost breakdown.",
    generate: serviceMethods.generateOperationsReport,
  },
  {
    value: 'payroll',
    type: 'PAYROLL',
    label: 'Payroll',
    title: 'Payroll Report',
    description:
      "What each person was paid for jobs whose last scheduled shift falls in the selected period, from the crew and drivers on each job's submitted cost breakdown.",
    generate: serviceMethods.generatePayrollReport,
  },
];

const PAGE_SIZE = 25;

const NO_FILTER_OPTIONS = { crews: [], employees: [] };

//Crews and people for narrowing a payroll run, offered only when a single location is selected,
//since a crew belongs to one branch and namesakes at two branches are not one person. The options
//are the crews that worked a job in the period and the people paid on those jobs, so they move with
//the period.
const PayrollFilter = ({
  options,
  loading,
  errorMessage,
  selectedCrewIds,
  selectedEmployees,
  employeeOptions,
  onCrewChange,
  onEmployeeChange,
}) => (
  <div className='flex flex-col gap-2'>
    <p className='text-sm font-semibold text-dark-blue'>Crews and employees</p>
    <div className='flex flex-wrap items-center gap-3'>
      <MultiSelectDropdown
        options={options.crews.map(crew => ({
          value: crew.id,
          label: crew.removed ? `${crew.name} (removed)` : crew.name,
        }))}
        selected={selectedCrewIds}
        onChange={onCrewChange}
        noun='crew'
        nounPlural='crews'
        emptyLabel='All crews'
        disabled={loading}
      />
      <MultiSelectDropdown
        options={employeeOptions.map(name => ({ value: name, label: name }))}
        selected={selectedEmployees}
        onChange={onEmployeeChange}
        noun='employee'
        nounPlural='employees'
        emptyLabel='All employees'
        disabled={loading}
      />
      {loading && <LoadingSpinner />}
    </div>
    {errorMessage ? (
      <p className='text-sm text-gunite-red'>{errorMessage}</p>
    ) : (
      <p className='text-sm text-opaque-job-details-gray'>
        A crew covers every job it worked a shift of in this period, and everyone paid on those
        jobs. Leave both empty to include everyone.
      </p>
    )}
  </div>
);

//a stored report's dates are calendar dates sent as UTC midnight, so take the date part rather than
//letting the browser shift it into the previous day
const datePart = value => String(value).slice(0, 10);

const periodOf = record =>
  describePeriod(datePart(record.startDate), datePart(record.endDate), record.createdAt);

const locationsLabel = locations =>
  locations.length === 1 ? locations[0].label : `${locations.length} locations`;

//a stored report's dates are already calendar dates, so the file name needs no time zone handling
const pdfFileName = (record, title) =>
  `${title.replace(/ /g, '_')}_${datePart(record.startDate)}_to_${datePart(record.endDate)}_run-${record.id}.pdf`;

//Rendering the PDF starts when the report opens, not on click, so the button only waits on a
//report that has just been opened.
const DownloadPDFButton = ({ record, title, period }) => {
  //PDFDownloadLink re-renders the document whenever it gets a new element, so keep one per record
  const pdf = useMemo(
    () => <ReportPDF record={record} title={title} period={period} />,
    [record.id],
  );

  return (
    <PDFDownloadLink
      document={pdf}
      fileName={pdfFileName(record, title)}
      className='flex justify-center items-center shrink-0 text-sm font-semibold py-2 px-3 w-40 h-[40px] rounded bg-white text-dark-blue border border-dark-blue hover:text-white hover:bg-dark-blue active:bg-white active:text-dark-blue'
    >
      {({ loading, error }) =>
        error ? 'PDF failed' : loading ? <LoadingSpinner /> : 'Download PDF'
      }
    </PDFDownloadLink>
  );
};

//Everything shown here comes from the stored record, never from the form, so an old report reads the
//same however the form is set and whatever has since changed about its branches.
const ReportView = ({ record }) => {
  const period = periodOf(record);
  const title = REPORT_TYPES.find(type => type.type === record.type)?.title;
  const sections = getSections(record.result, period);
  const isPayroll = record.type === 'PAYROLL';

  //one request serves every branch's job list, made the first time any of them is opened
  const [jobsDetail, setJobsDetail] = useState({ status: 'idle' });

  const loadJobs = async () => {
    if (jobsDetail.status === 'loading' || jobsDetail.status === 'ready') return;
    setJobsDetail({ status: 'loading' });

    try {
      const res = await serviceMethods.getReportJobs(record.id);
      const data = await res.json();

      if (!res.ok) {
        setJobsDetail({
          status: 'error',
          error: data.detail || data.message || 'An unknown error occurred.',
        });
        return;
      }

      setJobsDetail({ status: 'ready', data });
    } catch (err) {
      setJobsDetail({ status: 'error', error: 'Network error: Could not load the jobs.' });
      console.error(err);
    }
  };

  return (
    <div className='w-full flex flex-col gap-6'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div>
          <h2 className='text-lg font-semibold text-dark-blue'>
            {title} · {period.label}
          </h2>
          <p className='text-sm text-opaque-job-details-gray'>{period.rangeLabel}</p>
          <p className='text-sm text-opaque-job-details-gray'>
            Run {formatRunAt(record.createdAt)}
            {record.requestedBy?.name ? ` by ${record.requestedBy.name}` : ''}. {STALENESS_NOTE}
          </p>
          {getFilterLines(record.result.filter).map(line => (
            <p key={line} className='text-sm font-semibold text-dark-blue'>
              {line}
            </p>
          ))}
        </div>
        <DownloadPDFButton record={record} title={title} period={period} />
      </div>

      <div className='w-full flex flex-col gap-6'>
        {sections.map(section => (
          <div key={section.key} className='w-full flex flex-col gap-6'>
            <div className='w-full h-px bg-navy-light' />
            {isPayroll ? (
              <PayrollSection
                heading={section.heading}
                subheading={section.subheading}
                totals={section.totals}
                coverage={section.coverage}
                employees={section.employees}
                contractors={section.contractors}
              />
            ) : (
              <ReportSection
                heading={section.heading}
                subheading={section.subheading}
                totals={section.totals}
                taxRates={section.taxRates}
                coverage={section.coverage}
                showYards={section.showYards}
                showBatches={section.showBatches}
                showContractors={section.showContractors}
                jobs={
                  section.key !== 'combined' && (
                    <ReportJobs
                      detail={jobsDetail}
                      onOpen={loadJobs}
                      locationId={section.key}
                      isPlaster={section.showBatches}
                    />
                  )
                }
              />
            )}
          </div>
        ))}

        <div className='w-full h-px bg-navy-light' />

        <div className='text-sm text-opaque-job-details-gray flex flex-col gap-1'>
          {(isPayroll ? PAYROLL_NOTES : OPERATIONS_NOTES).map(note => (
            <p key={note}>{note}</p>
          ))}
        </div>
      </div>
    </div>
  );
};

const PastReports = ({ history, loading, errorMessage, openId, openingId, onOpen, onPage }) => {
  const { reports, page, total } = history;
  const firstShown = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(page * PAGE_SIZE, total);
  const [expanded, setExpanded] = useState(true);

  return (
    <section
      className={`flex flex-col w-full gap-4 mb-6 bg-opacity-10 bg-navy-bright rounded-lg border border-navy-light px-2 min-[400px]:px-5 pt-4 ${
        expanded ? 'pb-6' : 'pb-4'
      }`}
    >
      <h2 className='text-lg font-semibold text-dark-blue'>
        <button
          type='button'
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
          className='flex items-center gap-2'
        >
          <FaChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? '' : '-rotate-90'}`}
          />
          Past reports
          {total > 0 && (
            <span className='text-sm font-normal text-opaque-job-details-gray'>({total})</span>
          )}
        </button>
      </h2>

      {expanded && errorMessage && (
        <div className='w-full p-4 rounded bg-red-100 text-red-700 border border-red-400'>
          {errorMessage}
        </div>
      )}

      {expanded && !errorMessage && !loading && !reports.length && (
        <p className='text-sm text-opaque-job-details-gray'>No reports have been run yet.</p>
      )}

      {expanded && reports.length > 0 && (
        <div className='w-full overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b border-navy-light text-left'>
                <th className='py-1.5 pl-3 pr-4 font-semibold'>Report</th>
                <th className='py-1.5 pr-4 font-semibold'>Period</th>
                <th className='py-1.5 pr-4 font-semibold'>Locations</th>
                <th className='py-1.5 pr-4 font-semibold'>Run by</th>
                <th className='py-1.5 font-semibold'>Run at</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(record => (
                <tr
                  key={record.id}
                  tabIndex={0}
                  onClick={() => onOpen(record.id)}
                  onKeyDown={e => e.key === 'Enter' && onOpen(record.id)}
                  className={`border-b border-navy-light cursor-pointer ${
                    record.id === openId ? 'bg-navy-light/60 font-semibold' : 'hover:bg-white/70'
                  }`}
                >
                  <td
                    className={`py-1.5 pl-3 pr-4 ${
                      record.id === openId ? 'shadow-[inset_4px_0_0_theme(colors.dark-blue)]' : ''
                    }`}
                  >
                    {REPORT_TYPES.find(type => type.type === record.type)?.label}
                    {record.id === openingId && ' …'}
                  </td>
                  <td className='py-1.5 pr-4'>{periodOf(record).label}</td>
                  <td
                    className='py-1.5 pr-4'
                    title={record.locations.map(location => location.label).join(', ')}
                  >
                    {locationsLabel(record.locations)}
                  </td>
                  <td className='py-1.5 pr-4'>{record.requestedBy?.name}</td>
                  <td className='py-1.5 whitespace-nowrap'>{formatRunAt(record.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expanded && total > PAGE_SIZE && (
        <div className='flex items-center justify-between text-sm text-opaque-job-details-gray'>
          <span>
            {firstShown}–{lastShown} of {total}
          </span>
          <div className='flex gap-2'>
            {[
              ['Previous', page - 1, page <= 1],
              ['Next', page + 1, lastShown >= total],
            ].map(([label, target, disabled]) => (
              <button
                key={label}
                type='button'
                disabled={disabled || loading}
                onClick={() => onPage(target)}
                className='px-3 h-[32px] rounded border border-navy-light bg-white text-dark-blue hover:bg-gray-light disabled:bg-disabled-gray disabled:text-opaque-job-details-gray'
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const AdminReports = () => {
  const { locationDropdownOptions, selectedScheduler } = useScheduler();
  const { me } = useAuth();

  const [granularity, setGranularity] = useState('month');
  const [period, setPeriod] = useState(() => getDefaultPeriod('month'));
  const [customRange, setCustomRange] = useState(() => {
    const { startDate, endDate } = getDefaultPeriod('month');
    return { startDate, endDate };
  });
  const [reportType, setReportType] = useState('operations');
  const [selectedLocationIds, setSelectedLocationIds] = useState([8]); //Prestige Gunite Austin, matching Timesheets
  const [loadingStarted, setLoadingStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [openReport, setOpenReport] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [history, setHistory] = useState({ reports: [], page: 1, total: 0 });
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [filterOptions, setFilterOptions] = useState(NO_FILTER_OPTIONS);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [filterOptionsError, setFilterOptionsError] = useState(null);
  const reportViewRef = useRef(null);

  const loadHistory = async page => {
    setHistoryLoading(true);
    setHistoryError(null);

    try {
      const res = await serviceMethods.getReports({ page, limit: PAGE_SIZE });
      const data = await res.json();

      if (!res.ok) {
        setHistoryError(data.detail || data.message || 'An unknown error occurred.');
        return;
      }

      setHistory({ reports: data.reports, page: data.page, total: data.total });
    } catch (err) {
      setHistoryError('Network error: Could not reach the server.');
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(1);
  }, []);

  //a scheduler or branch admin can still be attached to a branch that has since been deleted, and
  //the API refuses to report on a deleted branch, so select all passes over it rather than failing
  //the whole run. an admin's list holds live branches only.
  const locationOptions = useMemo(() => {
    const deletedIds = new Set(
      (me?.scheduler ?? []).filter(item => item.location?.deletedAt).map(item => item.location.id),
    );
    return locationDropdownOptions.map(option =>
      deletedIds.has(option.value) ? { ...option, skipSelectAll: true } : option,
    );
  }, [locationDropdownOptions, me?.scheduler]);

  //the picker offers only the user's own branches, and the API refuses any other, so a scheduler
  //elsewhere would otherwise be refused the default Austin report without seeing it selected
  useEffect(() => {
    if (!locationOptions.length) return;

    const offered = new Set(locationOptions.map(option => option.value));
    setSelectedLocationIds(ids => {
      if (ids.some(id => offered.has(id))) return ids.filter(id => offered.has(id));
      const current = selectedScheduler?.location?.id;
      const fallback = locationOptions.find(option => !option.skipSelectAll) ?? locationOptions[0];
      return [offered.has(current) ? current : fallback.value];
    });
  }, [locationOptions, selectedScheduler?.location?.id]);

  //a report opens below the history table, which can be a long way down the page
  useEffect(() => {
    if (openReport) reportViewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [openReport?.id]);

  const handleRun = async () => {
    setLoadingStarted(true);
    setErrorMessage(null);

    try {
      const res = await activeType.generate({
        locationIds: selectedLocationIds,
        startDate: activePeriod.startDate,
        endDate: activePeriod.endDate,
        ...(isPayroll && {
          crewIds: canFilterPayroll ? selectedCrewIds : [],
          employees: canFilterPayroll ? selectedEmployees : [],
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setErrorMessage(data.detail || data.message || 'An unknown error occurred.');
        return;
      }

      setOpenReport(data.report);
      //the new run is the newest, so it heads the first page
      loadHistory(1);
    } catch (err) {
      setErrorMessage('Network error: Could not reach the server.');
      console.error(err);
    } finally {
      setLoadingStarted(false);
    }
  };

  const handleOpen = async reportId => {
    if (reportId === openReport?.id || openingId) return;

    setOpeningId(reportId);
    setHistoryError(null);

    try {
      const res = await serviceMethods.getReport(reportId);
      const data = await res.json();

      if (!res.ok) {
        setHistoryError(data.detail || data.message || 'An unknown error occurred.');
        return;
      }

      setOpenReport(data.report);
    } catch (err) {
      setHistoryError('Network error: Could not reach the server.');
      console.error(err);
    } finally {
      setOpeningId(null);
    }
  };

  //an open report carries its own type, period and locations, so changing the form leaves it be;
  //only an error from the last run stops applying
  const clearError = () => setErrorMessage(null);

  const activeType = REPORT_TYPES.find(type => type.value === reportType);

  const handleReportTypeChange = value => {
    setReportType(value);
    clearError();
  };

  //crews belong to one branch, so a different selection starts the filter over
  const handleLocationChange = ids => {
    setSelectedLocationIds(ids);
    setSelectedCrewIds([]);
    setSelectedEmployees([]);
    clearError();
  };

  const isCustom = granularity === 'custom';
  const periods = useMemo(() => (isCustom ? [] : getPeriods(granularity)), [granularity, isCustom]);

  const customIsValid = isValidCustomRange(customRange.startDate, customRange.endDate);
  //the period the Run button will actually use, or null while a custom range is incomplete
  const activePeriod = isCustom
    ? (customIsValid && buildCustomPeriod(customRange.startDate, customRange.endDate)) || null
    : period;

  const isPayroll = reportType === 'payroll';
  const canFilterPayroll = isPayroll && selectedLocationIds.length === 1 && !!activePeriod;
  const filterLocationId = canFilterPayroll ? selectedLocationIds[0] : null;

  useEffect(() => {
    if (!filterLocationId) return;

    //a slower response for a period since changed must not overwrite the current one
    let stale = false;
    setFilterOptionsLoading(true);
    setFilterOptionsError(null);

    (async () => {
      try {
        const res = await serviceMethods.getPayrollFilterOptions({
          locationId: filterLocationId,
          startDate: activePeriod.startDate,
          endDate: activePeriod.endDate,
        });
        const data = await res.json();
        if (stale) return;

        if (!res.ok) {
          setFilterOptions(NO_FILTER_OPTIONS);
          setFilterOptionsError(data.detail || data.message || 'An unknown error occurred.');
          return;
        }

        const options = { crews: data.crews, employees: data.employees };
        setFilterOptions(options);
        //keep whatever is still on offer in the new period, and only that
        setSelectedCrewIds(ids => ids.filter(id => options.crews.some(crew => crew.id === id)));
        setSelectedEmployees(names => names.filter(name => options.employees.includes(name)));
      } catch (err) {
        if (stale) return;
        setFilterOptions(NO_FILTER_OPTIONS);
        setFilterOptionsError('Network error: Could not load crews and employees.');
        console.error(err);
      } finally {
        if (!stale) setFilterOptionsLoading(false);
      }
    })();

    return () => {
      stale = true;
    };
  }, [filterLocationId, activePeriod?.startDate, activePeriod?.endDate]);

  //with crews picked, only the people paid on their jobs can be picked
  const employeeOptions = useMemo(() => {
    if (!selectedCrewIds.length) return filterOptions.employees;
    const names = new Set(
      filterOptions.crews
        .filter(crew => selectedCrewIds.includes(crew.id))
        .flatMap(crew => crew.employees),
    );
    return filterOptions.employees.filter(name => names.has(name));
  }, [filterOptions, selectedCrewIds]);

  const handleCrewChange = ids => {
    setSelectedCrewIds(ids);
    if (ids.length) {
      const names = new Set(
        filterOptions.crews.filter(crew => ids.includes(crew.id)).flatMap(crew => crew.employees),
      );
      setSelectedEmployees(current => current.filter(name => names.has(name)));
    }
    clearError();
  };

  const handleEmployeeChange = names => {
    setSelectedEmployees(names);
    clearError();
  };

  const handleGranularityChange = value => {
    //custom opens on the dates you were already looking at; the rest land on their last complete
    //period rather than on nothing
    if (value === 'custom') {
      setCustomRange({ startDate: period.startDate, endDate: period.endDate });
    } else {
      setPeriod(getDefaultPeriod(value));
    }
    setGranularity(value);
    clearError();
  };

  const handleCustomChange = (field, value) => {
    setCustomRange(current => ({ ...current, [field]: value }));
    clearError();
  };

  const handlePeriodChange = id => {
    setPeriod(periods.find(option => option.id === id));
    clearError();
  };

  return (
    <div>
      <h1 className='text-xl font-semibold text-dark-blue mb-6'>Reports</h1>
      <section className='flex flex-col w-full gap-6 mb-6 items-start justify-between bg-opacity-10 bg-navy-bright rounded-lg border border-navy-light px-2 min-[400px]:px-5 pt-4 pb-8'>
        <div className='flex flex-col w-full justify-between gap-6'>
          <div className='flex flex-col gap-2'>
            <p className='text-sm font-semibold text-dark-blue'>Report type</p>
            <div className='flex flex-wrap gap-6'>
              {REPORT_TYPES.map(type => (
                <div key={type.value}>
                  <input
                    type='radio'
                    id={`reportType-${type.value}`}
                    name='reportType'
                    className='mt-[-2px]'
                    value={type.value}
                    checked={reportType === type.value}
                    disabled={loadingStarted}
                    onChange={() => handleReportTypeChange(type.value)}
                  />
                  <label
                    htmlFor={`reportType-${type.value}`}
                    className='text-dark-blue text-sm font-semibold ml-[10px]'
                  >
                    {type.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <h2 className='text-lg font-semibold text-dark-blue'>{activeType.title}</h2>
          <p className='text-sm text-opaque-job-details-gray'>{activeType.description}</p>

          <div className='flex flex-col gap-2'>
            <p className='text-sm font-semibold text-dark-blue'>Report period</p>
            <div className='flex w-fit rounded border border-navy-light overflow-hidden bg-white'>
              {GRANULARITIES.map(option => (
                <button
                  key={option.value}
                  type='button'
                  onClick={() => handleGranularityChange(option.value)}
                  className={`px-4 h-[40px] text-sm font-[450] border-r border-navy-light last:border-r-0 ${
                    granularity === option.value
                      ? 'bg-dark-blue text-white'
                      : 'text-dark-blue hover:bg-gray-light'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {isCustom ? (
              <div className='flex flex-wrap items-end gap-3'>
                {[
                  ['startDate', 'From'],
                  ['endDate', 'To'],
                ].map(([field, label]) => (
                  <label key={field} className='flex flex-col gap-1'>
                    <span className='text-sm text-opaque-job-details-gray'>{label}</span>
                    <input
                      type='date'
                      value={customRange[field]}
                      onChange={e => handleCustomChange(field, e.target.value)}
                      className='h-[40px] px-3 rounded border border-navy-light bg-white text-sm text-dark-blue focus:border-dark-blue focus:ring-0'
                    />
                  </label>
                ))}
              </div>
            ) : (
              <div className='flex flex-wrap items-center gap-3'>
                <SelectDropdown
                  options={periods.map(option => ({ value: option.id, label: option.label }))}
                  selected={period.id}
                  onChange={handlePeriodChange}
                  emptyLabel='Select a period'
                />
              </div>
            )}

            <p className='text-sm text-opaque-job-details-gray'>
              {activePeriod ? (
                activePeriod.rangeLabel
              ) : (
                <span className='text-gunite-red'>
                  The From date must not be after the To date.
                </span>
              )}
            </p>
          </div>

          <div className='flex flex-col gap-2'>
            <p className='text-sm font-semibold text-dark-blue'>Locations</p>
            <MultiSelectDropdown
              options={locationOptions}
              selected={selectedLocationIds}
              onChange={handleLocationChange}
              noun='location'
              nounPlural='locations'
              emptyLabel='Select locations'
            />
          </div>

          {canFilterPayroll && (
            <PayrollFilter
              options={filterOptions}
              loading={filterOptionsLoading}
              errorMessage={filterOptionsError}
              selectedCrewIds={selectedCrewIds}
              selectedEmployees={selectedEmployees}
              employeeOptions={employeeOptions}
              onCrewChange={handleCrewChange}
              onEmployeeChange={handleEmployeeChange}
            />
          )}

          {isPayroll && selectedLocationIds.length > 1 && (
            <p className='text-sm text-opaque-job-details-gray'>
              Every crew and employee is included when more than one location is selected. Select a
              single location to narrow the report to particular crews or employees.
            </p>
          )}
        </div>

        <div className='flex w-full justify-center gap-4 pb-4'>
          <button
            onClick={handleRun}
            disabled={
              loadingStarted ||
              !selectedLocationIds.length ||
              !activePeriod ||
              (canFilterPayroll && filterOptionsLoading)
            }
            type='button'
            className='flex justify-center items-center text-sm font-semibold py-2 px-3 w-40 h-[40px] rounded bg-white text-dark-blue border border-dark-blue hover:text-white hover:bg-dark-blue active:bg-white active:text-dark-blue disabled:bg-disabled-gray disabled:text-opaque-job-details-gray disabled:border-navy-light'
          >
            {loadingStarted ? <LoadingSpinner /> : 'Run Report'}
          </button>
        </div>

        {errorMessage && (
          <div className='w-full p-4 rounded bg-red-100 text-red-700 border border-red-400'>
            {errorMessage}
          </div>
        )}
      </section>

      <PastReports
        history={history}
        loading={historyLoading}
        errorMessage={historyError}
        openId={openReport?.id}
        openingId={openingId}
        onOpen={handleOpen}
        onPage={loadHistory}
      />

      {openReport && (
        <section
          ref={reportViewRef}
          className='flex flex-col w-full gap-6 mb-6 bg-opacity-10 bg-navy-bright rounded-lg border border-navy-light px-2 min-[400px]:px-5 pt-4 pb-8 scroll-mt-4'
        >
          <ReportView key={openReport.id} record={openReport} />
        </section>
      )}
    </div>
  );
};

export default AdminReports;
